import React, { PureComponent } from 'react';
import { loadModules } from 'esri-loader'

import { Loading } from '../Loading/Loading';

import { AgolService } from '../../services/Agol';

import './CardMap.scss'

export interface CardMapProps {
    initialSearch?: string;
    webMapTitle?: string;
    webMap: string;
    onSearchResult?: __esri.SearchSelectResultEventHandler;
    camadaBusca: string,
    campoBusca: string,
    searchPlaceholder?: string,
}

export interface CardMapState {
    loadingVisible: boolean;
}

export class CardMap extends PureComponent<CardMapProps, CardMapState> {
    mapView?: __esri.MapView;
    searchFeatureLayer?: __esri.FeatureLayer;
    searchWidget!: __esri.widgetsSearch;
    searchLocatorSource?: __esri.LayerSearchSource;

    mapLoading?: boolean;
    mapLoaded?: boolean;
    initialSearchRequested?: boolean;

    get destroyed() {
        return this.reloadMapId < 0;
    }

    private loadingCount = 0;

    private reloadMapId = 0;
    private webmapId = '';

    private lastSearchLayerUrl?: string;

    private mapModulesPromise?: Promise<any>;

    mapRef = React.createRef<HTMLDivElement>();

    get hasSearchProps() {
        return !!(this.props.camadaBusca && this.props.campoBusca);
    }

    constructor(props) {
        super(props);
        this.state = {
            loadingVisible: false
        };
    }

    componentDidMount() {
        this.reloadMapIfNeeded(++this.reloadMapId);
    }

    componentWillUpdate(nextProps: Readonly<CardMapProps>, nextState: Readonly<CardMapState>, nextContext: any) {
        const checkChange = (source, target) => {
            const keys = Object.keys(source);
            for (let key of keys) {
                const sourceValue = source[key];
                const targetValue = target[key];
                if (sourceValue !== targetValue) {
                    return true;
                }
            }

            return false;
        };

        if (checkChange(this.props, nextProps)) {
            this.reloadMapIfNeeded(++this.reloadMapId);
        }
    }

    componentWillUnmount() {
        this.reloadMapId = -1;
        this.mapLoading = false;
        this.mapLoaded = false;
        if (this.mapView) {
            this.mapView.destroy();
            this.mapView.container = null as any;
            this.mapView = undefined;
        }
    }

    private showLoading() {
        ++this.loadingCount;
        if (!this.state.loadingVisible && this.loadingCount > 0) {
            this.setState({ loadingVisible: true });
        }
    }

    private hideLoading() {
        let loadingCount = this.loadingCount - 1;
        loadingCount = Math.max(loadingCount, 0);
        if (this.loadingCount !== loadingCount) {
            this.loadingCount = loadingCount;
            this.setState({ loadingVisible: false });
        }
    }

    render() {
        return (
            <div className='customCardMap'>
                <div className='cardMapTitle'>{this.props.webMapTitle}</div>
                <div className='customWebMap'>
                    {!this.state.loadingVisible ? '' : (
                        <div className='loading-overlay'>
                            <Loading visible={this.state.loadingVisible} />
                        </div>
                    )}
                    <div
                        id='legend-container'
                        className='legend-container d-none'
                    ></div>
                    <div className='webmap' ref={this.mapRef} />
                </div>
            </div>
        )
    }

    reloadMapIfNeeded(reloadMapId: number) {
        this.showLoading();
        if (!this.mapModulesPromise) {
            this.mapModulesPromise = loadModules(
                [
                    'esri/config',
                    'esri/WebMap',
                    'esri/views/MapView',
                    'esri/widgets/Legend',
                    'esri/widgets/Search',
                    'esri/widgets/Search/LayerSearchSource',
                    'esri/layers/FeatureLayer',
                    'esri/symbols/SimpleLineSymbol',
                    'esri/tasks/IdentifyTask',
                    'esri/tasks/support/IdentifyParameters'
                ],
                {
                    version: '4.16',
                    css: true,
                    dojoConfig: {
                        locale: 'pt-br',
                    },
                }
            );
        }

        return this.mapModulesPromise.then(
            async ([
                esriConfig,
                WebMap,
                MapView,
                Legend,
                Search,
                LayerSearchSource,
                FeatureLayer,
                SimpleLineSymbol,
            ]: [__esri.config, __esri.WebMapConstructor, __esri.MapViewConstructor, __esri.LegendConstructor,
                    __esri.widgetsSearchConstructor, __esri.LayerSearchSourceConstructor, __esri.FeatureLayerConstructor,
                    __esri.SimpleLineSymbolConstructor]) => {
                if (this.reloadMapId !== reloadMapId) {
                    return;
                }
                const reloadSearchAttributesAsync = async (webMapChanged = false) => {
                    this.searchWidget.allPlaceholder = this.props.searchPlaceholder ?? '';

                    this.lastSearchLayerUrl = this.props.camadaBusca;

                    if (this.props.camadaBusca) {
                        if (!this.searchFeatureLayer || webMapChanged) {
                            if (this.searchFeatureLayer) {
                                this.searchFeatureLayer.destroy();
                            }
                            this.searchFeatureLayer = new FeatureLayer({
                                url: this.props.camadaBusca,
                                outFields: ['*'],
                                visible: false
                            });
                        }

                        this.searchLocatorSource = new LayerSearchSource({
                            layer: this.searchFeatureLayer,
                            placeholder: this.props.searchPlaceholder,
                            name: 'A FeatureLayer',
                            prefix: '',
                            suffix: '',
                            outFields: ['*'],
                            maxResults: 1,
                            maxSuggestions: 6,
                            exactMatch: true,
                            searchFields: [this.props.campoBusca],
                            displayField: this.props.campoBusca,
                            minSuggestCharacters: 0,
                            resultSymbol: new SimpleLineSymbol({
                                color: '#00ffff',
                                width: '2px',
                                style: 'solid',
                            } as any),
                        });
                        this.searchWidget.sources = [this.searchLocatorSource] as any;

                        if (!this.initialSearchRequested && this.props.initialSearch) {
                            this.searchWidget.search(this.props.initialSearch);
                        }
                    } else {
                        if (this.searchFeatureLayer) {
                            this.searchFeatureLayer.destroy();
                            this.searchFeatureLayer = undefined;
                        }
                        this.searchLocatorSource = undefined;
                        this.searchWidget.sources = [] as any;
                    }
                };

                if (!this.mapLoaded && !this.mapLoading) {
                    if (!this.props.webMap) {
                        return;
                    }
                    try {
                        this.mapLoading = true;

                        esriConfig.portalUrl = 'https://dadosgeociencias.ibge.gov.br/portal';
                        const webmap = new WebMap({
                            portalItem: {
                                id: this.props.webMap,
                            },
                        });

                        this.mapView = new MapView({
                            container: this.mapRef.current as any,
                            map: webmap,
                        });

                        const legendContainer = document.getElementById('legend-container') as HTMLDivElement;
                        new Legend({
                            container: legendContainer as any,
                            view: this.mapView,
                            style: {
                                type: 'classic',
                                layout: 'auto',
                            },
                        });

                        const legendBtnToggle: HTMLDivElement = document.createElement('div');
                        legendBtnToggle.title = 'Legenda';
                        legendBtnToggle.className =
                            'esri-icon esri-icon-layer-list esri-widget--button esri-widget esri-interactive';
                        legendBtnToggle.addEventListener('click', () => {
                            if (!legendContainer.classList.contains('d-none')) {
                                legendContainer.classList.add('d-none');
                            } else {
                                legendContainer.classList.remove('d-none');
                            }
                        });

                        this.mapView.ui.add(legendBtnToggle, 'top-left');

                        this.searchWidget = new Search({
                            container: document.getElementsByClassName('search-box')[0] as any,
                            view: this.mapView,
                            resultGraphicEnabled: true,
                            popupEnabled: false,
                            locationEnabled: true,
                            includeDefaultSources: false,
                        });

                        this.searchWidget.on('select-result', (e) => {
                            if (!this.mapLoaded) {
                                return;
                            }

                            // if (!this.initialSearchRequested) {
                            //     this.searchWidget.searchTerm = '';
                            // }
                            this.initialSearchRequested = true;
                            if (this.props.onSearchResult) {
                                this.props.onSearchResult(e);
                            }
                        });

                        const view = this.mapView;
                        this.mapView.on('click', async (e) => {
                            if (!this.mapLoaded || !this.hasSearchProps || this.state.loadingVisible) {
                                return;
                            }

                            const reloadMapId = this.reloadMapId;
                            this.showLoading();
                            try {
                                const response = await AgolService.getFeatureByPoint(this.props.camadaBusca, e.mapPoint, this.props.campoBusca);
                                if (this.reloadMapId !== reloadMapId) {
                                    return;
                                }

                                const aux = (response?.data?.features ?? [])[0]?.attributes[this.props.campoBusca];
                                if (aux) {
                                    await this.searchWidget.search(aux);
                                }
                            } finally {
                                this.hideLoading();
                            }
                        });

                        await this.mapView.when();

                        if (this.reloadMapId === reloadMapId) {
                            await reloadSearchAttributesAsync();
                        }

                        if (this.destroyed) {
                            return;
                        }

                        this.mapLoaded = true;
                    } finally {
                        this.mapLoading = false;
                    }
                } else {
                    await this.mapView?.when();

                    if (this.reloadMapId !== reloadMapId) {
                        return;
                    }

                    const webMapChanged = this.props.webMap !== this.webmapId;
                    if (webMapChanged) {
                        this.webmapId = this.props.webMap;
                        if (this.mapView) {
                            this.mapView.map = new WebMap({
                                portalItem: {
                                    id: this.props.webMap,
                                },
                            });

                            await this.mapView?.when();

                            if (this.reloadMapId !== reloadMapId) {
                                return;
                            }
                        }
                    }

                    await reloadSearchAttributesAsync(webMapChanged);
                }
            }
        )
            .finally(() => {
                this.hideLoading();
            });
    }
}